import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

const Dashboard = () => {
    const [stats, setStats] = useState({
        users: 0,
        speakers: 0,
        agenda: 0,
        questions: 0,
        sessions: 0,
        votes: 0,
        topPages: [],
        recentLogs: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [users, speakers, agenda, questions, sessionStats] = await Promise.all([
                    api.get('/users'),
                    api.get('/speakers'),
                    api.get('/agenda'),
                    api.get('/questions'),
                    api.get('/sessions/stats')
                ]);
                setStats({
                    users: users.data.length,
                    speakers: speakers.data.length,
                    agenda: agenda.data.length,
                    questions: questions.data.length,
                    sessions: sessionStats.data.totalEvents || 0,
                    votes: 0, // We could fetch total votes if needed
                    topPages: sessionStats.data.topPages || [],
                    recentLogs: sessionStats.data.recentLogs || []
                });
            } catch (error) {
                console.error("Error fetching stats", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, []);

    const statCards = [
        { label: 'Usuarios Registrados', value: stats.users, icon: '👥', color: 'blue' },
        { label: 'Conferencistas', value: stats.speakers, icon: '🎙️', color: 'indigo' },
        { label: 'Charlas Programadas', value: stats.agenda, icon: '📅', color: 'emerald' },
        { label: 'Interacciones', value: stats.sessions, icon: '⚡', color: 'orange' },
    ];

    const getActionLabel = (type) => {
        return type === 'login' ? 'Inicio de Sesión' : 'Navegación';
    };

    return (
        <div className="animate-fadeIn">
            <header className="mb-10">
                <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Dashboard General</h1>
                <p className="text-slate-500 mt-2">Bienvenido al panel de administración de NeuroUpdate.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {statCards.map((card, idx) => (
                    <div 
                        key={idx} 
                        className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                    >
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-sm`} style={{ backgroundColor: `${card.color === 'blue' ? '#eff6ff' : card.color === 'indigo' ? '#eef2ff' : card.color === 'emerald' ? '#ecfdf5' : '#fff7ed'}` }}>
                            {card.icon}
                        </div>
                        <div>
                            <p className="text-slate-400 font-medium text-sm uppercase tracking-wider">{card.label}</p>
                            <h2 className="text-3xl font-black text-slate-900 mt-1">{isLoading ? '...' : card.value}</h2>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Traffic Stats */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 lg:col-span-2">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <span className="text-2xl">📈</span> Tráfico por Sección
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {stats.topPages.map((page, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-black">0{idx + 1}</div>
                                    <span className="font-bold text-slate-700 capitalize">{page.page_name || 'Inicio'}</span>
                                </div>
                                <span className="text-xs font-black bg-white px-3 py-1 rounded-full text-slate-400 shadow-sm border border-slate-100">{page.count} visits</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Event Promo Card */}
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-10 rounded-3xl shadow-xl text-white relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-8 transform group-hover:scale-110 transition-transform duration-500 opacity-20 pointer-events-none">
                        <span className="text-9xl">🚀</span>
                    </div>
                    <h3 className="text-3xl font-black mb-4 leading-tight">Interacción en Vivo</h3>
                    <p className="text-blue-100 text-lg mb-8">Monitorea las votaciones y preguntas de los asistentes en tiempo real.</p>
                    <div className="flex gap-4">
                        <button 
                            onClick={() => navigate('/questions')}
                            className="bg-white text-blue-600 px-8 py-3.5 rounded-xl font-bold hover:bg-blue-50 transition-all shadow-lg active:scale-95 text-sm"
                        >
                            Ver Preguntas
                        </button>
                        <button 
                            onClick={() => navigate('/resultados')}
                            className="bg-blue-500 text-white px-8 py-3.5 rounded-xl font-bold hover:bg-blue-400 transition-all shadow-lg active:scale-95 text-sm border border-blue-400"
                        >
                            Ver Votación
                        </button>
                    </div>
                </div>

                {/* Recent Activity (Audit Log) */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 lg:col-span-3">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <span className="text-2xl">🕒</span> Actividad de Usuarios
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] border-b border-slate-50">
                                    <th className="pb-4 pl-4">Usuario</th>
                                    <th className="pb-4">Acción</th>
                                    <th className="pb-4">Página</th>
                                    <th className="pb-4 text-right pr-4">Fecha/Hora</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {stats.recentLogs.map((log) => (
                                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors group">
                                        <td className="py-4 pl-4">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-700">{log.name} {log.last_name}</span>
                                                <span className="text-[10px] text-slate-400 font-medium">{log.email}</span>
                                            </div>
                                        </td>
                                        <td className="py-4">
                                            <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-wider ${log.action_type === 'login' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                                {getActionLabel(log.action_type)}
                                            </span>
                                        </td>
                                        <td className="py-4 font-medium text-slate-500 capitalize">{log.page_name || '-'}</td>
                                        <td className="py-4 text-right pr-4 text-slate-400 text-xs font-medium">
                                            {new Date(log.timestamp).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {stats.recentLogs.length === 0 && (
                            <p className="text-center py-10 text-slate-400 italic">No hay actividad registrada aún.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
